import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
  role: string;
  order_position: number;
}

interface FAQSectionProps {
  compact?: boolean;
  maxItems?: number;
}

export const FAQSection: React.FC<FAQSectionProps> = ({ compact = false, maxItems }) => {
  const { profile } = useAuth();
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [filteredFaqs, setFilteredFaqs] = useState<FAQItem[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFAQs();
  }, [profile?.role]);

  useEffect(() => {
    filterFAQs();
  }, [searchQuery, selectedCategory, faqs]);

  const loadFAQs = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('faq_items')
        .select('*')
        .eq('is_published', true);

      if (profile?.role) {
        query = query.in('role', [profile.role, 'both']);
      }

      const { data, error } = await query.order('order_position');

      if (error) throw error;
      setFaqs(data || []);
    } catch (error) {
      console.error('Error loading FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterFAQs = () => {
    let filtered = faqs;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(faq => faq.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(faq =>
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query) ||
        faq.category.toLowerCase().includes(query)
      );
    }

    if (maxItems) {
      filtered = filtered.slice(0, maxItems);
    }

    setFilteredFaqs(filtered);
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const categories = Array.from(new Set(faqs.map(faq => faq.category)));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!compact && (
        <>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600">
              Find answers to common questions about VendorBoom
            </p>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              All Categories
            </button>
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </>
      )}

      {filteredFaqs.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-slate-600">No FAQs found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredFaqs.map(faq => (
            <div
              key={faq.id}
              className="bg-white border border-slate-200 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => toggleExpand(faq.id)}
                className="w-full text-left p-4 hover:bg-slate-50 transition-colors flex items-start justify-between"
              >
                <div className="flex-1 pr-4">
                  <h3 className="font-semibold text-slate-900 mb-1">
                    {faq.question}
                  </h3>
                  {!compact && (
                    <span className="text-xs text-slate-500">{faq.category}</span>
                  )}
                </div>
                {expandedId === faq.id ? (
                  <ChevronUp className="w-5 h-5 text-slate-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-500 flex-shrink-0" />
                )}
              </button>

              {expandedId === faq.id && (
                <div className="px-4 pb-4 border-t border-slate-100">
                  <p className="text-slate-700 pt-3">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {compact && maxItems && faqs.length > maxItems && (
        <div className="text-center">
          <button className="text-slate-600 hover:text-slate-900 font-medium text-sm">
            View All FAQs →
          </button>
        </div>
      )}
    </div>
  );
};
